import { useEffect, useState } from "react";
import { loadWebSession, type WebSession } from "./lib/authSession";
import { ENGINE_API_KEY } from "./lib/config";
import { DESIGN_GALLERY_ENABLED, isDesignGalleryPath } from "./lib/designGallery";
import { isE2eMockMode } from "./lib/e2eFixture";
import { App } from "./App";
import { AuthPage } from "./pages/AuthPage";
import { DesignGalleryPage } from "./pages/DesignGalleryPage";

/**
 * Browser SPA: require email/password session
 * (unless API key demo, or Playwright e2e mock mode ?e2e=1).
 * The product shell is the only app.
 */
export function RootApp() {
  const [path, setPath] = useState(() => window.location.pathname);
  const [session, setSession] = useState<WebSession | null>(() => loadWebSession());

  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  useEffect(() => {
    const sync = () => setSession(loadWebSession());
    window.addEventListener("sc-session-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sc-session-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Constant is false in a production build, so this branch (and the page)
  // drops out of that bundle. An unknown path then 404s in the shell.
  // Both effects stay above this return: leaving the gallery for /rfq
  // re-renders the same component, and a hook after the return changes order.
  if (DESIGN_GALLERY_ENABLED && isDesignGalleryPath(path)) {
    return <DesignGalleryPage />;
  }

  const hasApiKey = !!ENGINE_API_KEY;
  const e2eMock = isE2eMockMode();
  const needsLogin = !session && !hasApiKey && !e2eMock;

  if (needsLogin) {
    return <AuthPage onLoggedIn={setSession} />;
  }

  return <App />;
}
